import {
  Check,
  ChatCircle,
  Clock,
  DotOutline,
  FileText,
  Flag,
  ImageSquare,
  Link as LinkIcon,
  PencilSimple,
  Plus,
  Sparkle,
  UserPlus,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { TaskSummary, TaskType } from '@mybusinessmate/schema';
import type { TagTone } from '@mybusinessmate/ui-tokens';
import { formatDay } from './project-display';

export const TASK_TYPE_META: Record<TaskType, { label: string; short: string; icon: PhosphorIcon; tone: TagTone }> = {
  post: { label: 'Instagram post', short: 'Post', icon: ImageSquare, tone: 'blue' },
  reel: { label: 'Reel', short: 'Reel', icon: Sparkle, tone: 'pink' },
  website: { label: 'Website build', short: 'Website build', icon: LinkIcon, tone: 'indigo' },
  monthly_update: { label: 'Website update', short: 'Website update', icon: PencilSimple, tone: 'indigo' },
  custom: { label: 'General', short: 'General', icon: FileText, tone: 'grey' },
};

interface StatusView {
  label: string;
  tone: TagTone;
  /** The list section this status sits under. */
  group: string;
}

/** What the agency calls each task status (Figma A05.2). Unknown statuses show as they are, under "Other". */
const STAFF_STATUS: Record<string, StatusView> = {
  'User Draft': { label: 'Draft', tone: 'grey', group: 'Drafts' },
  Requested: { label: 'Requested', tone: 'grey', group: 'Needs triage' },
  Pending: { label: 'Queued', tone: 'grey', group: 'Queued' },
  'In Progress': { label: 'In progress', tone: 'blue', group: 'In progress' },
  'In Review': { label: 'Awaiting approval', tone: 'amber', group: 'Waiting for client' },
  'Revision Requested': { label: 'Changes requested', tone: 'amber', group: 'Changes requested' },
  'On Hold': { label: 'On hold', tone: 'amber', group: 'On hold' },
  Completed: { label: 'Done', tone: 'green', group: 'Done' },
  Cancelled: { label: 'Cancelled', tone: 'grey', group: 'Done' },
};

/** What the client is told (Sprint 2 KB "Client labels"; Figma C03.2). */
const CLIENT_STATUS: Record<string, StatusView> = {
  Requested: { label: 'Waiting to start', tone: 'grey', group: 'Queued' },
  Pending: { label: 'Waiting to start', tone: 'grey', group: 'Queued' },
  'In Progress': { label: 'In progress', tone: 'grey', group: 'In progress' },
  'On Hold': { label: 'In progress', tone: 'grey', group: 'In progress' },
  'In Review': { label: 'Review', tone: 'blue', group: 'Waiting for you' },
  'Revision Requested': { label: 'Changes asked', tone: 'amber', group: 'Changes requested' },
  Completed: { label: 'Done', tone: 'green', group: 'Done' },
  Cancelled: { label: 'Cancelled', tone: 'grey', group: 'Done' },
};

export const staffStatus = (status: string): StatusView =>
  STAFF_STATUS[status] ?? { label: status, tone: 'grey', group: 'Other' };
export const clientStatus = (status: string): StatusView =>
  CLIENT_STATUS[status] ?? { label: status, tone: 'grey', group: 'Other' };

/** Section order on the staff project page: what needs a person first. */
export const STAFF_GROUP_ORDER = [
  'Waiting for client',
  'Changes requested',
  'In progress',
  'Needs triage',
  'Drafts',
  'Queued',
  'On hold',
  'Done',
  'Other',
] as const;

/** Section order on the client project page: what needs them first. */
export const CLIENT_GROUP_ORDER = [
  'Waiting for you',
  'Changes requested',
  'In progress',
  'Queued',
  'Done',
  'Other',
] as const;

/** Splits tasks into ordered, non-empty sections; tasks keep the (recent-first) order they arrived in. */
export function groupTasks(
  tasks: readonly TaskSummary[],
  view: (status: string) => StatusView,
  order: readonly string[],
): { title: string; tasks: TaskSummary[] }[] {
  const byGroup = new Map<string, TaskSummary[]>();
  for (const task of tasks) {
    const group = view(task.status).group;
    byGroup.set(group, [...(byGroup.get(group) ?? []), task]);
  }
  return order.flatMap((title) => (byGroup.has(title) ? [{ title, tasks: byGroup.get(title)! }] : []));
}

const weekday = new Intl.DateTimeFormat('en-IN', { weekday: 'short', timeZone: 'UTC' });
const MS_PER_DAY = 86_400_000;

/** "Fri 31 Oct": a calendar date with its weekday, as the client pages write dates. */
export const weekdayDay = (iso: string) => `${weekday.format(new Date(iso))} ${formatDay(iso)}`;

/** "due Fri 31 Oct"; flagged urgent (amber) when it is due today or tomorrow, or already late. Stored as UTC-midnight days. */
export function dueLabel(deadline: string | null, now: Date = new Date()): { text: string; urgent: boolean } | null {
  if (!deadline) return null;
  const due = new Date(deadline);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const days = Math.round((due.getTime() - today) / MS_PER_DAY);
  return { text: `due ${weekday.format(due)} ${formatDay(deadline)}`, urgent: days <= 1 };
}

export const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Low' },
  // TODO(api): Figma also has "Urgent"; `project_priority_enum` has three values.
  { value: 'Medium', label: 'Normal' },
  { value: 'High', label: 'High' },
] as const;

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const dayMonthLocal = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const timeOfDay = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/** "16 Oct, 6:02 PM": the design's date-and-time wording (day first, 12-hour clock). */
export const formatDateTime = (iso: string) => {
  const date = new Date(iso);
  return `${dayMonthLocal.format(date)}, ${timeOfDay.format(date)}`;
};

/** "just now", "5 min ago", "2 h ago"; anything older than a day is a date and time. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const elapsed = Math.max(0, now.getTime() - new Date(iso).getTime());
  if (elapsed < MS_PER_MINUTE) return 'just now';
  if (elapsed < MS_PER_HOUR) return `${Math.floor(elapsed / MS_PER_MINUTE)} min ago`;
  if (elapsed < 24 * MS_PER_HOUR) return `${Math.floor(elapsed / MS_PER_HOUR)} h ago`;
  return formatDateTime(iso);
}

/** The icon beside an activity line, chosen from what it says (the feed stores wording, not a kind). */
export function activityIcon(message: string): PhosphorIcon {
  if (/^moved progress/i.test(message)) return Clock;
  if (/milestone/i.test(message)) return Flag;
  if (/^(asked for changes|sent .* back|the client (asked|changed))/i.test(message)) return ChatCircle;
  if (/^(approved v\d|the client approved)/i.test(message)) return Check;
  if (/^(added|removed|suggested|approved|declined|closed)\b/i.test(message)) return UserPlus;
  if (/^(created|saved)/i.test(message)) return Plus;
  if (/^submitted/i.test(message)) return FileText;
  if (/^changed/i.test(message)) return PencilSimple;
  return DotOutline;
}
